import { getOwner, onCleanup, runWithOwner } from 'solid-js';

function focusable(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  return [...container.querySelectorAll<HTMLElement>(selector)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

export function trapFocus(
  container: HTMLElement,
  options: { initialFocus?: HTMLElement } = {},
): () => void {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const focusIsInAnotherModal = (): boolean => {
    const active = document.activeElement;
    if (!active || container.contains(active)) return false;
    return !!active.closest('[aria-modal="true"], dialog[open]');
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    if (focusIsInAnotherModal()) return;

    const items = focusable(container);
    if (items.length === 0) {
      e.preventDefault();
      container.focus();
      return;
    }

    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;

    if (e.shiftKey && (active === first || !container.contains(active))) {
      e.preventDefault();
      last.focus();
      return;
    }
    if (!e.shiftKey && (active === last || !container.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const target = options.initialFocus ?? focusable(container)[0] ?? container;
  if (target === container && !container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
  }
  target.focus();

  document.addEventListener('keydown', onKeyDown, true);

  return () => {
    document.removeEventListener('keydown', onKeyDown, true);
    previouslyFocused?.focus?.();
  };
}

export function useModalTrap(el: HTMLElement): void;
export function useModalTrap(options: {
  initialFocus: () => HTMLElement | undefined;
}): (el: HTMLElement) => void;
export function useModalTrap(
  arg: HTMLElement | { initialFocus: () => HTMLElement | undefined },
): ((el: HTMLElement) => void) | undefined {
  if (!(arg instanceof HTMLElement)) return (el) => attach(el, arg.initialFocus);
  attach(arg);
  return undefined;
}

function attach(el: HTMLElement, initialFocus?: () => HTMLElement | undefined): void {
  const owner = getOwner();
  queueMicrotask(() => {
    const release = trapFocus(el, { initialFocus: initialFocus?.() });
    if (owner) runWithOwner(owner, () => onCleanup(release));
    else el.addEventListener('modal-closed', release, { once: true });
  });
}
